import { useState, useEffect, useCallback, useRef } from "react";
import { num, fmt, str, Pill, EditButton, Chevron, labelStyle } from "./pmUi";

// Pakkumine tab: the client's cost list (Excel), each row linked to a plan material, with a unit-price calculation
// per row. Built from the "Pakkumine (desktop)" mockup. Linked volumes become the expected m³ in Ülevaade.
const API = "/api";
const GRID = "64px minmax(200px, 1fr) 56px 76px 88px 96px 104px 232px";
const CALC_GRID = "minmax(160px, 1fr) 152px 152px 88px 40px";

const n0 = v => num(v) || 0;

function defaultCalc() {
  return {
    oh: "10", profit: "8",
    lines: [
      { id: 1, kind: "mach", name: "", a: "", b: "" },
      { id: 2, kind: "haul", name: "Vedu", a: "", b: "" },
      { id: 3, kind: "other", name: "Tööjõud ja muu", a: "", b: "" },
    ],
  };
}

function volumeOf(r) {
  if (r.unit === "m³") return r.qty;
  const h = num(r.h);
  if (r.unit === "m²" && h) return (r.qty * h) / 100;
  return null;
}

async function call(path, site, opts = {}) {
  const r = await fetch(`${API}${path}${path.includes("?") ? "&" : "?"}site=${encodeURIComponent(site)}`, { credentials: "same-origin", ...opts });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Viga ${r.status}`);
  return r.json();
}

const postJson = (path, site, body) =>
  call(path, site, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

function UploadButton({ c, label, onFile, busy, primary }) {
  const input = useRef(null);
  return (
    <>
      <input ref={input} type="file" accept=".xlsx,.xlsm" style={{ display: "none" }}
        onChange={e => { const f = e.target.files[0]; e.target.value = ""; if (f) onFile(f); }} />
      <button onClick={() => input.current.click()} disabled={busy} style={{
        height: 40, padding: "0 16px", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600,
        color: primary ? c.onAccent : c.text, background: primary ? c.accent : "transparent",
        border: `1px solid ${primary ? c.accent : c.inBorder}`, borderRadius: 9, cursor: "pointer", fontFamily: "inherit",
      }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 9.5V2.5M4 5l3-3 3 3M2.5 11.5h9" /></svg>
        <span>{busy ? "Loen faili…" : label}</span>
      </button>
    </>
  );
}

function CalcModal({ c, mobile, site, row, materials, machines, onClose, onSaved }) {
  const [calc, setCalc] = useState(() => row.calc || defaultCalc());
  const [editing, setEditing] = useState(!row.calc);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = e => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const unit = row.unit;
  const mat = row.material_id ? materials.find(m => m.id === row.material_id) : null;
  const dens = mat?.density || 0;
  const buy = mat?.price_t || 0;
  const hf = unit === "m²" ? n0(row.h) / 100 : unit === "m³" ? 1 : 0;
  const warns = [];

  let matVal = 0, matNote, matColor = c.muted;
  if (!mat) {
    matNote = row.no_material ? "Rida on märgitud „Pole vaja”, materjali kulu ei arvestata" : "Materjal on tabelis määramata";
    if (!row.no_material) warns.push("Materjal on määramata, omakulu on seetõttu alahinnatud.");
  } else if (hf === 0) {
    matNote = `Ühiku ${unit} jaoks pole mahtu${unit === "m²" ? ", sisesta paksus h" : ""}`;
    matColor = c.warn;
    warns.push("Materjali kulu ei saa arvutada, sest rea ühikul puudub maht.");
  } else if (!buy || !dens) {
    matNote = "Ostuhind või tihedus puudub Ülevaates";
    matColor = c.warn;
    warns.push("Materjali ostuhind või tihedus puudub, sisesta need Ülevaate materjalitabelis.");
  } else {
    matVal = buy * dens * hf;
    matNote = `${fmt(buy, 2)} €/t × ${fmt(dens, 2)} t/m³${unit === "m²" ? ` × ${fmt(n0(row.h), 0)} cm` : ""}`;
  }

  const lineVal = l => {
    const a = n0(l.a), b = n0(l.b);
    if (l.kind === "mach") return b > 0 ? a / b : 0;
    if (l.kind === "haul") return a * b * dens * hf;
    return a;
  };
  if (calc.lines.some(l => l.kind === "haul" && n0(l.a)) && !dens) warns.push("Veo arvutus vajab materjali tihedust.");
  const sub = matVal + calc.lines.reduce((s, l) => s + lineVal(l), 0);
  const ohAmt = (sub * n0(calc.oh)) / 100;
  const cost = sub + ohAmt;
  const profitAmt = (cost * n0(calc.profit)) / 100;
  const suggested = cost + profitAmt;
  const bid = row.price || 0;
  const margin = bid - cost;
  const neg = margin < 0;
  const marginColor = bid > 0 && neg ? "#F87171" : c.text;

  const setLine = (id, patch) => setCalc(k => ({ ...k, lines: k.lines.map(l => (l.id === id ? { ...l, ...patch } : l)) }));
  const addLine = kind => setCalc(k => {
    const id = k.lines.reduce((m, l) => Math.max(m, l.id), 0) + 1;
    const base = kind === "mach" ? { name: "", a: "", b: "" } : kind === "haul" ? { name: "Vedu", a: "", b: "" } : { name: "Muu kulu", a: "", b: "" };
    return { ...k, lines: [...k.lines, { id, kind, ...base }] };
  });

  const toggle = async () => {
    if (!editing) { setEditing(true); return; }
    setSaving(true);
    try {
      await postJson(`/pm/offer/rows/${row.id}/calc`, site, calc);
      onSaved(row.id, calc);
      setEditing(false);
      setError("");
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const input = (value, onChange, label, width = 80, align = "right", list) => (
    <input type="text" inputMode={align === "right" ? "decimal" : "text"} value={value} readOnly={!editing} list={list}
      onChange={e => editing && onChange(e.target.value)} aria-label={label}
      style={{
        width, height: 44, boxSizing: "border-box", padding: "0 8px", textAlign: align, fontSize: 14, fontWeight: 600,
        background: editing ? c.inBg : "transparent", border: `1px solid ${editing ? c.inBorder : "transparent"}`, borderRadius: 8,
        color: c.text, fontFamily: "inherit",
      }} />
  );
  const sumRow = (label, value, extra, strong) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12, minHeight: extra ? 48 : 40, fontSize: 14 }}>
      <span style={{ flexGrow: 1, color: strong ? c.text : c.soft, fontWeight: strong ? 600 : 400 }}>{label}</span>
      {extra}
      <span style={{ width: 96, textAlign: "right", fontWeight: strong ? 700 : 600 }}>{value}</span>
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 9999, background: c.overlay, display: "flex",
      alignItems: "center", justifyContent: "center", padding: mobile ? 8 : 24, boxSizing: "border-box" }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label="Ühikhinna kalkulatsioon" style={{
        width: 860, maxWidth: "100%", maxHeight: "92vh", overflowY: "auto", boxSizing: "border-box", background: c.card,
        border: `1px solid ${c.cardBorder}`, borderRadius: 14, color: c.text, boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: mobile ? "16px 16px 12px" : "20px 24px 16px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flex: "1 1 260px" }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted }}>ÜHIKHINNA KALKULATSIOON</span>
            <span style={{ fontSize: 18, fontWeight: 700 }}>{row.name}</span>
            <span style={{ fontSize: 12, color: c.muted }}>{row.code || "ilma koodita"} · {unit} · maht {fmt(row.qty)}</span>
          </div>
          <EditButton c={c} editing={editing} saving={saving} onClick={toggle} />
          <button onClick={onClose} aria-label="Sulge" style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
            background: "transparent", border: `1px solid ${c.inBorder}`, borderRadius: 9, cursor: "pointer" }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={c.soft} strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M3 3l8 8M11 3l-8 8" /></svg>
          </button>
        </div>
        {error && <div style={{ margin: "0 24px 12px", color: c.error, fontSize: 13 }}>{error}</div>}

        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 640 }}>
            <div style={{ display: "grid", gridTemplateColumns: CALC_GRID, columnGap: 12, alignItems: "end", padding: "10px 24px", background: c.head,
              borderTop: `1px solid ${c.rowBorder}`, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted }}>
              <div>Komponent</div><div>Hind</div><div>Tootlikkus / kaugus</div><div style={{ textAlign: "right" }}>€ / {unit}</div><div />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: CALC_GRID, columnGap: 12, alignItems: "center", padding: "8px 24px", minHeight: 60,
              boxSizing: "border-box", borderTop: `1px solid ${c.rowBorder}` }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 3, gridColumn: "1 / 4" }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{mat ? `Materjal: ${mat.name}` : "Materjal"}</span>
                <span style={{ fontSize: 12, color: matColor }}>{matNote}</span>
              </div>
              <div style={{ textAlign: "right", fontSize: 14, fontWeight: 700 }}>{fmt(matVal, 2)}</div>
              <div />
            </div>
            {calc.lines.map(l => {
              const units = l.kind === "mach" ? ["€/h", `${unit}/h`] : l.kind === "haul" ? ["€/t·km", "km"] : [`€/${unit}`, null];
              const placeholder = l.kind === "mach" ? "Masin" : l.kind === "haul" ? "Vedu" : "Kulu";
              return (
                <div key={l.id} style={{ display: "grid", gridTemplateColumns: CALC_GRID, columnGap: 12, alignItems: "center", padding: "8px 24px",
                  minHeight: 60, boxSizing: "border-box", borderTop: `1px solid ${c.rowBorder}` }}>
                  <div>
                    {editing ? input(l.name, v => {
                      const m = l.kind === "mach" && machines.find(x => x.name === v);
                      setLine(l.id, { name: v, ...(m && m.rate != null && !l.a ? { a: String(m.rate) } : {}) });
                    }, "Komponendi nimi", "100%", "left", l.kind === "mach" ? "pm-machines" : undefined)
                      : <span style={{ fontSize: 14, fontWeight: 600, padding: "0 8px" }}>{l.name || placeholder}</span>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {input(l.a, v => setLine(l.id, { a: v }), `Hind: ${l.name || placeholder}`)}
                    <span style={{ fontSize: 12, color: c.muted }}>{units[0]}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {units[1] && <>{input(l.b, v => setLine(l.id, { b: v }), `Tootlikkus või kaugus: ${l.name || placeholder}`)}
                      <span style={{ fontSize: 12, color: c.muted }}>{units[1]}</span></>}
                  </div>
                  <div style={{ textAlign: "right", fontSize: 14, fontWeight: 700 }}>{fmt(lineVal(l), 2)}</div>
                  <div>
                    {editing && (
                      <button onClick={() => setCalc(k => ({ ...k, lines: k.lines.filter(x => x.id !== l.id) }))} aria-label="Eemalda komponent"
                        style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent",
                          border: `1px solid ${c.inBorder}`, borderRadius: 8, cursor: "pointer" }}>
                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke={c.soft} strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M3 3l8 8M11 3l-8 8" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <datalist id="pm-machines">{machines.map(m => <option key={m.name} value={m.name} />)}</datalist>

        {editing && (
          <div style={{ display: "flex", gap: 8, padding: "12px 24px", borderTop: `1px solid ${c.rowBorder}`, flexWrap: "wrap" }}>
            {[["mach", "+ Masin"], ["haul", "+ Vedu"], ["other", "+ Muu kulu"]].map(([k, label]) => (
              <button key={k} onClick={() => addLine(k)} style={{ height: 40, padding: "0 14px", fontSize: 13, fontWeight: 600, color: c.accent,
                background: "transparent", border: `1px solid ${c.accent}`, borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>{label}</button>
            ))}
          </div>
        )}
        {warns.length > 0 && (
          <div style={{ padding: "12px 24px", borderTop: `1px solid ${c.rowBorder}`, fontSize: 12, color: c.warn, background: c.warnSoft }}>
            {[...new Set(warns)].join(" ")}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", padding: "8px 24px 4px", background: c.head, borderTop: `1px solid ${c.rowBorder}` }}>
          {sumRow("Omakulu (materjal, masinad, vedu, muu)", fmt(sub, 2))}
          {sumRow("Üldkulu", fmt(ohAmt, 2), <>{input(calc.oh, v => setCalc(k => ({ ...k, oh: v })), "Üldkulu protsent", 64)}<span style={{ fontSize: 12, color: c.muted }}>%</span></>)}
          <div style={{ borderTop: `1px solid ${c.rowBorder}` }}>{sumRow("Omakulu kokku", fmt(cost, 2), null, true)}</div>
          {sumRow("Kasum", fmt(profitAmt, 2), <>{input(calc.profit, v => setCalc(k => ({ ...k, profit: v })), "Kasumi protsent", 64)}<span style={{ fontSize: 12, color: c.muted }}>%</span></>)}
          <div style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 44, fontSize: 15, borderTop: `1px solid ${c.rowBorder}` }}>
            <span style={{ flexGrow: 1, fontWeight: 700 }}>Soovituslik ühikhind</span>
            <span style={{ fontSize: 12, color: c.muted }}>€ / {unit}</span>
            <span style={{ width: 96, textAlign: "right", fontWeight: 800, color: c.accent }}>{fmt(suggested, 2)}</span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", padding: "4px 24px 12px", background: c.head }}>
          <div style={{ borderTop: `1px solid ${c.rowBorder}` }}>{sumRow("Pakkumise ühikhind", bid > 0 ? fmt(bid, 2) : "—")}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 40, fontSize: 14 }}>
            <span style={{ flexGrow: 1, color: c.soft }}>Marginaal omakulu suhtes</span>
            <span style={{ fontSize: 12, color: marginColor }}>{bid > 0 ? `${neg ? "−" : ""}${fmt(Math.abs((margin / bid) * 100), 1)}%` : ""}</span>
            <span style={{ width: 96, textAlign: "right", fontWeight: 700, color: marginColor }}>{bid > 0 ? fmt(margin, 2) : "—"}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 40, fontSize: 14 }}>
            <span style={{ flexGrow: 1, color: c.soft }}>Rea marginaal kokku ({fmt(row.qty)} {unit})</span>
            <span style={{ width: 96, textAlign: "right", fontWeight: 700, color: marginColor }}>{bid > 0 ? `${fmt(margin * row.qty)} €` : "—"}</span>
          </div>
          <div style={{ paddingTop: 8, fontSize: 12, color: c.muted }}>Materjali hind ja tihedus tulevad Ülevaate materjalitabelist, neid muudetakse seal.</div>
        </div>
      </div>
    </div>
  );
}

export default function PmOffer({ site, c, mobile }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [q, setQ] = useState("");
  const [flt, setFlt] = useState("all");
  const [menu, setMenu] = useState(null);
  const [newName, setNewName] = useState("");
  const [calcRow, setCalcRow] = useState(null);
  const [open, setOpen] = useState(true);

  const load = useCallback(() => {
    call("/pm/offer", site).then(d => { setData(d); setError(""); }).catch(e => setError(e.message));
  }, [site]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data) return;
    setMaterials(data.materials);
    if (!editing) setRows((data.offer?.rows || []).map(r => ({ ...r, h: str(r.h) })));
  }, [data, editing]);

  const upload = async file => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await call("/pm/offer/upload", site, { method: "POST", body: fd });
      setEditing(false);
      load();
    } catch (e) { setError(e.message); }
    setUploading(false);
  };

  const setRow = (id, patch) => setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const toggleEdit = async () => {
    if (!editing) { setEditing(true); return; }
    setSaving(true);
    try {
      await postJson("/pm/offer/rows", site, {
        rows: rows.map(r => ({ id: r.id, h: num(r.h), material_id: r.material_id, no_material: !!r.no_material })),
      });
      setEditing(false);
      setMenu(null);
      load();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const addMaterial = async rowId => {
    const name = newName.trim();
    if (!name) return;
    try {
      const m = await postJson("/pm/materials/new", site, { name });
      setMaterials(ms => [...ms, m]);
      setRow(rowId, { material_id: m.id, no_material: false });
      setMenu(null);
      setNewName("");
    } catch (e) { setError(e.message); }
  };

  if (error && !data) return <div style={{ color: c.error, fontSize: 13 }}>{error}</div>;
  if (!data) return <div style={{ padding: 40, textAlign: "center", color: c.muted }}>Laadin…</div>;

  const card = { background: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 14, boxShadow: c.shadow };
  const offer = data.offer;

  const fileCard = (
    <section style={{ ...card, padding: mobile ? "14px 16px" : "16px 22px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: c.pill, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke={c.accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 2h7l3 3v11H4z" /><path d="M11 2v3h3M6.5 9h5M6.5 12h5" /></svg>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, flex: "1 1 240px" }}>
        <span style={{ fontSize: 14, fontWeight: 600, overflowWrap: "anywhere" }}>{offer ? offer.filename : "Pakkumist pole veel üles laetud"}</span>
        <span style={{ fontSize: 12, color: c.muted }}>
          {offer ? `${offer.summary} · kontrolli read enne kasutamist`
            : "Lae üles kululoend (.xlsx). Veerud leitakse päise järgi: töö nimetus, ühik, maht, ühikhind."}
        </span>
      </div>
      <UploadButton c={c} label={offer ? "Lae uus pakkumine" : "Lae pakkumine"} onFile={upload} busy={uploading} primary={!offer} />
    </section>
  );
  const errorBox = error && (
    <div style={{ background: c.errorSoft, color: c.error, padding: "10px 14px", borderRadius: 10, fontSize: 13 }}>{error}</div>
  );
  if (!offer) return <>{errorBox}{fileCard}</>;

  // ─── figures ───
  const matName = id => (materials.find(m => m.id === id) || {}).name || "";
  let total = 0, linkedSum = 0, linkedCount = 0, openCount = 0, assigned = 0;
  const perMat = {};
  const secSums = offer.sections.map(() => 0);
  const built = rows.map(r => {
    const sum = r.price ? r.qty * r.price : 0;
    total += sum;
    secSums[r.sec] = (secSums[r.sec] || 0) + sum;
    const vol = volumeOf(r);
    let key = "open";
    if (r.material_id) {
      key = "linked";
      linkedSum += sum;
      linkedCount += 1;
      if (vol !== null) {
        perMat[r.material_id] = (perMat[r.material_id] || 0) + vol;
        assigned += vol;
      }
    } else if (r.no_material) key = "nomat";
    else openCount += 1;
    return { r, sum, vol, key };
  });
  const counts = { all: built.length, open: 0, linked: 0, nomat: 0 };
  built.forEach(b => { counts[b.key] += 1; });
  const qn = q.trim().toLowerCase();
  const shown = built.filter(b => (flt === "all" || b.key === flt) && (!qn || `${b.r.code} ${b.r.name}`.toLowerCase().includes(qn)));
  const linkedPct = total > 0 ? Math.round((linkedSum / total) * 100) : 0;
  const matSummary = materials.filter(m => perMat[m.id]);

  const head = { display: "grid", gridTemplateColumns: GRID, columnGap: 12, alignItems: "end", padding: "10px 22px", background: c.head,
    borderTop: `1px solid ${c.rowBorder}`, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted };
  const optBtn = (label, active, onClick, accentOutline) => (
    <button key={label} onClick={onClick} style={{
      width: 232, height: 44, boxSizing: "border-box", padding: "0 12px", display: "flex", alignItems: "center", fontSize: 13, fontWeight: 600,
      textAlign: "left", cursor: "pointer", fontFamily: "inherit", borderRadius: 8,
      background: active ? c.pill : accentOutline ? "transparent" : c.inBg,
      border: `1px solid ${active || accentOutline ? c.accent : c.inBorder}`, color: active || accentOutline ? c.accent : c.text,
    }}>{label}</button>
  );

  const tableRows = [];
  let lastSec = -1;
  shown.forEach(({ r, sum, vol, key }) => {
    if (r.sec !== lastSec) {
      lastSec = r.sec;
      tableRows.push(
        <div key={`s${r.sec}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 22px", background: c.head,
          borderTop: `1px solid ${c.rowBorder}`, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: c.soft }}>
          <span>{offer.sections[r.sec]}</span>
          <span style={{ flexGrow: 1 }} />
          <span style={{ color: c.text }}>{fmt(secSums[r.sec])} €</span>
        </div>
      );
    }
    const isOpen = menu && menu.row === r.id;
    let matText = "Määramata", matColor = c.warn, sub = "", subColor = c.muted;
    if (key === "linked") {
      matText = matName(r.material_id);
      matColor = c.text;
      if (vol !== null) sub = `${fmt(vol)} m³`;
      else if (r.unit === "m²") { sub = "Sisesta paksus h"; subColor = c.warn; }
      else { sub = "m³ puudub"; subColor = c.warn; }
    } else if (key === "nomat") { matText = "Pole vaja"; matColor = c.soft; }
    tableRows.push(
      <div key={r.id} style={{ display: "grid", gridTemplateColumns: GRID, columnGap: 12, alignItems: "center", padding: "8px 22px", minHeight: 60,
        boxSizing: "border-box", background: isOpen ? c.pill : "transparent", borderTop: `1px solid ${c.rowBorder}` }}>
        <div style={{ fontSize: 13, color: c.muted, overflowWrap: "anywhere" }}>{r.code}</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{r.name}</div>
        <div style={{ fontSize: 13, color: c.soft }}>{r.unit}</div>
        <div>
          {r.unit === "m²" && (editing ? (
            <input type="text" inputMode="decimal" value={r.h} onChange={e => setRow(r.id, { h: e.target.value })} placeholder="h"
              aria-label={`Kihi paksus cm: ${r.name}`}
              style={{ width: "100%", height: 44, boxSizing: "border-box", padding: "0 8px", textAlign: "right", fontSize: 14, fontWeight: 600,
                background: c.inBg, border: `1px solid ${c.inBorder}`, borderRadius: 8, color: c.text, fontFamily: "inherit" }} />
          ) : <div style={{ textAlign: "right", fontSize: 14, fontWeight: 600, padding: "0 8px" }}>{r.h ? `${r.h}` : "—"}</div>)}
        </div>
        <div style={{ textAlign: "right", fontSize: 14, fontWeight: 600 }}>{fmt(r.qty)}</div>
        <button onClick={() => !editing && setCalcRow(r.id)} disabled={editing} aria-label={`Ühikhinna kalkulatsioon: ${r.name}`}
          title={editing ? "Salvesta read enne kalkulatsiooni" : "Ühikhinna kalkulatsioon"}
          style={{ width: "100%", minHeight: 44, boxSizing: "border-box", padding: "0 4px", display: "flex", alignItems: "center",
            justifyContent: "flex-end", gap: 6, background: "transparent", border: 0, borderRadius: 8, fontSize: 14, color: c.soft,
            cursor: editing ? "default" : "pointer", fontFamily: "inherit" }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={r.calc ? c.accent : c.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2.5" y="1.5" width="9" height="11" rx="1.5" /><path d="M4.5 4.5h5M4.5 7.5h1M7 7.5h1M4.5 10h1M7 10h1" /></svg>
          <span>{r.price ? fmt(r.price, 2) : "—"}</span>
        </button>
        <div style={{ textAlign: "right", fontSize: 14, fontWeight: 700 }}>{r.price ? fmt(sum) : "—"}</div>
        <button onClick={() => { if (!editing) return; setMenu(isOpen ? null : { row: r.id, mode: "list" }); setNewName(""); }}
          aria-expanded={!!isOpen} aria-label={`Materjal: ${r.name}`}
          style={{ width: "100%", minHeight: 44, boxSizing: "border-box", padding: "4px 10px", display: "flex", alignItems: "center", gap: 8,
            textAlign: "left", background: editing ? c.inBg : "transparent", border: `1px solid ${isOpen ? c.accent : editing ? c.inBorder : "transparent"}`,
            borderRadius: 8, color: c.text, cursor: editing ? "pointer" : "default", fontFamily: "inherit" }}>
          <span style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: matColor }}>{matText}</span>
            {sub && <span style={{ fontSize: 11, color: subColor }}>{sub}</span>}
          </span>
          {editing && <Chevron open={!!isOpen} color={c.muted} />}
        </button>
      </div>
    );
    if (isOpen) {
      const pick = patch => { setRow(r.id, patch); setMenu(null); };
      tableRows.push(
        <div key={`m${r.id}`} style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, padding: "0 22px 8px", background: c.pill }}>
          {menu.mode === "new" ? (
            <div style={{ display: "flex", gap: 8 }}>
              <input autoFocus type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Uue materjali nimi"
                aria-label="Uue materjali nimi" onKeyDown={e => e.key === "Enter" && addMaterial(r.id)}
                style={{ width: 164, height: 44, boxSizing: "border-box", padding: "0 10px", fontSize: 13, fontWeight: 600, background: c.inBg,
                  border: `1px solid ${c.inBorder}`, borderRadius: 8, color: c.text, fontFamily: "inherit" }} />
              <button onClick={() => addMaterial(r.id)} style={{ width: 60, height: 44, fontSize: 13, fontWeight: 600, color: c.onAccent,
                background: c.accent, border: `1px solid ${c.accent}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>Lisa</button>
            </div>
          ) : (<>
            {materials.map(m => optBtn(m.name, r.material_id === m.id, () => pick({ material_id: m.id, no_material: false })))}
            {optBtn("Pole vaja", !!r.no_material, () => pick({ material_id: null, no_material: true }))}
            {optBtn("Määramata", !r.material_id && !r.no_material, () => pick({ material_id: null, no_material: false }))}
            {optBtn("+ Loo uus materjal", false, () => setMenu({ row: r.id, mode: "new" }), true)}
          </>)}
        </div>
      );
    }
  });

  const kpiCard = { ...card, padding: mobile ? "16px" : "20px 22px" };
  const calcData = calcRow && rows.find(r => r.id === calcRow);

  return (
    <>
      {errorBox}
      {fileCard}
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 16, alignItems: "start" }}>
        <section style={kpiCard}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted }}>PAKKUMISE SUMMA</span>
            <Pill c={c}>{linkedCount}/{rows.length} rida seotud</Pill>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 14 }}>
            <span style={{ fontSize: mobile ? 32 : 42, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1 }}>{fmt(total)}</span>
            <span style={{ fontSize: mobile ? 14 : 16, fontWeight: 600, color: c.mutedLight }}>€</span>
          </div>
          <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", background: c.track, margin: "16px 0 14px" }}>
            <div style={{ height: "100%", background: c.accent, width: `${linkedPct}%` }} />
          </div>
          {[["Materjalidega read", c.accent, linkedPct, linkedSum], ["Ilma materjalita / määramata", c.track, total > 0 ? 100 - linkedPct : 0, total - linkedSum]]
            .map(([label, color, pct, val], i) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, padding: i ? "10px 0 0" : "10px 0", borderTop: `1px solid ${c.rowBorder}`, fontSize: 13 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
                <span style={{ fontWeight: 600 }}>{label}</span>
                <span style={{ flexGrow: 1, fontSize: 11, color: c.muted }}>{pct}%</span>
                <span style={{ fontWeight: 700 }}>{fmt(val)} €</span>
              </div>
            ))}
        </section>
        <section style={kpiCard}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted }}>MÄÄRATUD MATERJAL</span>
            <Pill c={c}>{openCount} rida määramata</Pill>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 14 }}>
            <span style={{ fontSize: mobile ? 32 : 42, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1 }}>{fmt(assigned)}</span>
            <span style={{ fontSize: mobile ? 14 : 16, fontWeight: 600, color: c.mutedLight }}>m³</span>
          </div>
          <div style={{ fontSize: 12, color: c.muted, margin: "10px 0 12px" }}>Salvestades läheb summa Ülevaate materjalitabelisse veergu „Oodatav”</div>
          {matSummary.length === 0 && (
            <div style={{ fontSize: 13, color: c.muted, borderTop: `1px solid ${c.rowBorder}`, padding: "10px 0 0" }}>
              Vajuta „Muuda” ja seo read materjalidega.
            </div>
          )}
          {matSummary.map(m => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderTop: `1px solid ${c.rowBorder}`, fontSize: 13 }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke={c.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 1.5l3.5 3.5L3 8.5" /></svg>
              <span style={{ fontWeight: 600 }}>{m.name}</span>
              <span style={{ flexGrow: 1 }} />
              <span style={{ fontWeight: 700 }}>{fmt(perMat[m.id])} m³</span>
            </div>
          ))}
        </section>
      </div>

      <section style={{ ...card, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 22px", flexWrap: "wrap" }}>
          <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, minHeight: 40 }}>
            <Chevron open={open} color={c.muted} />
            <span style={labelStyle(c)}>Pakkumise read</span>
          </button>
          <EditButton c={c} editing={editing} saving={saving} onClick={toggleEdit} />
          {editing && <button onClick={() => { setEditing(false); setMenu(null); }} style={{ all: "unset", cursor: "pointer", fontSize: 13, color: c.soft, padding: "10px 6px" }}>Tühista</button>}
          <span style={{ flexGrow: 1 }} />
          <Pill c={c}>{fmt(total)} €</Pill>
        </div>
        {open && (<>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 22px 14px", flexWrap: "wrap" }}>
            <div style={{ position: "relative", width: mobile ? "100%" : 320 }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={c.muted} strokeWidth="1.6" strokeLinecap="round" aria-hidden="true" style={{ position: "absolute", left: 12, top: 15 }}><circle cx="6" cy="6" r="4" /><path d="M9.2 9.2L12 12" /></svg>
              <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Otsi nimetust või jrk nr" aria-label="Otsi pakkumise ridu"
                style={{ width: "100%", height: 44, boxSizing: "border-box", padding: "0 12px 0 34px", fontSize: 14, background: c.inBg,
                  border: `1px solid ${c.inBorder}`, borderRadius: 9, color: c.text, fontFamily: "inherit" }} />
            </div>
            {[["all", "Kõik"], ["open", "Määramata"], ["linked", "Seotud"], ["nomat", "Pole vaja"]].map(([k, label]) => (
              <button key={k} onClick={() => setFlt(k)} aria-pressed={flt === k} style={{
                height: 40, padding: "0 14px", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: flt === k ? 700 : 500, borderRadius: 8,
                border: `1px solid ${flt === k ? c.accent + "44" : c.cardBorder}`, background: flt === k ? c.accent + "18" : "transparent",
                color: flt === k ? c.accent : c.muted, cursor: "pointer", fontFamily: "inherit",
              }}>
                <span>{label}</span><span style={{ fontWeight: 500, opacity: 0.75 }}>{counts[k]}</span>
              </button>
            ))}
          </div>
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: 1000 }}>
              <div style={head}>
                <div>Jrk nr</div><div>Töö nimetus</div><div>Ühik</div><div style={{ textAlign: "right" }}>h cm</div>
                <div style={{ textAlign: "right" }}>Maht</div><div style={{ textAlign: "right" }}>Ühikhind €</div>
                <div style={{ textAlign: "right" }}>Summa €</div><div>Materjal</div>
              </div>
              {tableRows}
              {shown.length === 0 && (
                <div style={{ padding: "28px 22px", borderTop: `1px solid ${c.rowBorder}`, fontSize: 14, color: c.muted }}>Ühtegi rida ei leitud. Muuda otsingut või filtrit.</div>
              )}
              <div style={{ ...head, alignItems: "center", padding: "14px 22px", fontSize: 14, fontWeight: 700, letterSpacing: 0, textTransform: "none", color: c.text }}>
                <div /><div>Kokku</div><div /><div /><div /><div /><div style={{ textAlign: "right" }}>{fmt(total)}</div><div />
              </div>
            </div>
          </div>
        </>)}
      </section>

      {calcData && (
        <CalcModal c={c} mobile={mobile} site={site} row={calcData} materials={materials} machines={data.machines}
          onClose={() => setCalcRow(null)}
          onSaved={(id, calc) => setRows(rs => rs.map(r => (r.id === id ? { ...r, calc } : r)))} />
      )}
    </>
  );
}
